/**
 * Stress marks for narration.
 *
 * Russian and Ukrainian narration rides on the combining acute accent (U+0301)
 * placed right after the stressed vowel: the narration planner adds it to words
 * that are easy to get wrong, and a reader can add it by hand when the planner
 * still says "поезд замЕр" instead of "зА́мер".
 *
 * The mark belongs to the SPOKEN text. Anything printed or fed to an image model
 * strips it — a page of a picture book should not carry accents the author did
 * not ask for.
 */
export const STRESS_MARK = '́';

/** Languages whose narration uses the mark at all — everywhere else it is noise. */
export const STRESS_LANGUAGES = ['ru', 'uk'] as const;
export function usesStressMarks(language: string | undefined | null): boolean {
  return !!language && (STRESS_LANGUAGES as readonly string[]).includes(language.slice(0, 2).toLowerCase());
}

/**
 * The text without stress marks — for pages, print, prompts and search.
 *
 * NFC first, and that is not a detail: French "café" and Czech "vzácný" can
 * arrive DECOMPOSED (e + U+0301), and a blind removal of every U+0301 would turn
 * them into "cafe" and "vzacny" — silently corrupting the story of a language
 * that never asked for stress marks. Normalising composes those into their own
 * letters (é, á), so only a mark standing on its own — ours — is left to remove.
 */
export function stripStress(text: string): string {
  if (!text.includes(STRESS_MARK)) return text;
  const composed = text.normalize('NFC');
  return composed.includes(STRESS_MARK) ? composed.replaceAll(STRESS_MARK, '') : composed;
}

/**
 * Toggle the mark for what the caret (or the selection) covers.
 *
 * - a SELECTION that contains marks → they are removed, so a whole word can be
 *   cleared in one press;
 * - a caret right after a marked vowel (or after the mark itself) → the mark goes;
 * - a caret right after an unmarked letter → the mark appears.
 *
 * Deleting a combining mark with Backspace is fiddly — it hides behind the letter —
 * so the button has to be able to take it off again.
 */
export function toggleStressAt(
  text: string,
  start: number,
  end: number = start,
): { text: string; caret: number } {
  const from = Math.max(0, Math.min(Math.min(start, end), text.length));
  const to = Math.max(0, Math.min(Math.max(start, end), text.length));

  if (to > from && text.slice(from, to).includes(STRESS_MARK)) {
    const cleaned = stripStress(text.slice(from, to));
    return { text: text.slice(0, from) + cleaned + text.slice(to), caret: from + cleaned.length };
  }

  // after the mark itself (the caret sits behind it) or before it (the caret is on
  // the vowel it belongs to) → remove that mark
  if (text[from - 1] === STRESS_MARK) {
    return { text: text.slice(0, from - 1) + text.slice(from), caret: from - 1 };
  }
  if (text[from] === STRESS_MARK) {
    return { text: text.slice(0, from) + text.slice(from + 1), caret: from };
  }

  const prev = text[from - 1];
  if (!prev || !/\p{L}/u.test(prev)) return { text, caret: from };
  return { text: text.slice(0, from) + STRESS_MARK + text.slice(from), caret: from + 1 };
}

/** Vowels that can carry a stress — Russian and Ukrainian. */
const VOWELS = 'аеёиоуыэюяіїєaeiouy';

export interface StressWord {
  /** The word as it is written, marks included. */
  text: string;
  /** Its start index in the whole text. */
  start: number;
  /** Absolute index of every vowel in it (marks excluded). */
  vowels: number[];
  /** Absolute index of the vowel that carries the mark, or null. */
  stressed: number | null;
}

/**
 * The words of a block, with their vowels — the model behind the stress picker.
 *
 * Typing U+0301 by hand is not something a parent can do, and pressing a button
 * with the caret in exactly the right place is barely better. The picker instead
 * offers the WORDS, and then the vowels of one word, so the mark is two taps and
 * never a keyboard trick.
 */
export function stressWords(text: string): StressWord[] {
  const out: StressWord[] = [];
  const re = /\p{L}[\p{L}\u0301'’-]*/gu;
  for (const m of text.matchAll(re)) {
    const start = m.index ?? 0;
    const word = m[0];
    const vowels: number[] = [];
    let stressed: number | null = null;
    for (let i = 0; i < word.length; i++) {
      const ch = word[i];
      if (ch === STRESS_MARK) {
        // the mark sits right after the vowel it belongs to
        if (vowels.length > 0) stressed = vowels[vowels.length - 1];
        continue;
      }
      if (VOWELS.includes(ch.toLowerCase())) vowels.push(start + i);
    }
    out.push({ text: word, start, vowels, stressed });
  }
  return out;
}

/**
 * Put the stress on the vowel at `vowelIndex`, or take it off when it is already
 * there. A word carries at most ONE stress, so any other mark inside the same
 * word goes — that is what makes the picker's second tap mean "move it here".
 */
export function setWordStress(text: string, vowelIndex: number): string {
  const word = stressWords(text).find((w) => w.vowels.includes(vowelIndex));
  if (!word) return text;
  const alreadyThere = word.stressed === vowelIndex;
  const end = word.start + word.text.length;
  const cleaned = stripStress(text.slice(word.start, end));
  if (alreadyThere) return text.slice(0, word.start) + cleaned + text.slice(end);
  // index inside the CLEANED word: count the non-mark characters before the vowel
  const offset = stripStress(text.slice(word.start, vowelIndex + 1)).length;
  const marked = cleaned.slice(0, offset) + STRESS_MARK + cleaned.slice(offset);
  return text.slice(0, word.start) + marked + text.slice(end);
}

/** @deprecated use toggleStressAt — kept for callers that only ever add. */
export function insertStressAt(text: string, caret: number): { text: string; caret: number } {
  return toggleStressAt(text, caret, caret);
}
